import { useEffect, useState } from 'react';
import { UserPlus, Check, X, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { superFetch, formatDate } from '@/lib/super-admin-api';

interface RegistrationRequest {
  id: string;
  nome_fantasia: string;
  cnpj: string | null;
  telefone: string;
  email: string;
  plano: string;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export function RegistrationRequestsPage() {
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pendente' | 'aprovado' | 'rejeitado'>('pendente');
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  async function fetchRequests() {
    setLoading(true);
    try {
      const res = await superFetch('/api/registration-requests');
      if (res.ok) {
        const data = await res.json();
        setRequests(data.data || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(id: string, status: 'aprovado' | 'rejeitado') {
    setProcessing(id);
    try {
      const res = await superFetch(`/api/registration-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(status === 'aprovado' ? 'Solicitação aprovada!' : 'Solicitação rejeitada');
        fetchRequests();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao processar');
      }
    } catch {
      toast.error('Erro de conexão');
    } finally {
      setProcessing(null);
    }
  }

  const filtered = filter === 'all' ? requests : requests.filter((r) => r.status === filter);
  const pendingCount = requests.filter((r) => r.status === 'pendente').length;

  const statusConfig: Record<string, { bg: string; color: string; label: string; icon: typeof Clock }> = {
    pendente: { bg: '#f59e0b20', color: '#f59e0b', label: 'Pendente', icon: Clock },
    aprovado: { bg: '#10b98120', color: '#10b981', label: 'Aprovado', icon: CheckCircle },
    rejeitado: { bg: '#ef444420', color: '#ef4444', label: 'Rejeitado', icon: XCircle },
  };

  const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>
            Solicitações de Cadastro
          </h1>
          <p className="text-base mt-1" style={{ color: '#94a3b8' }}>
            {pendingCount > 0
              ? `${pendingCount} solicitação${pendingCount > 1 ? 'ões' : ''} pendente${pendingCount > 1 ? 's' : ''}`
              : 'Nenhuma solicitação pendente'}
          </p>
        </div>
        {pendingCount > 0 && (
          <div
            className="flex items-center gap-2 rounded-full px-4 py-2"
            style={{ background: '#f59e0b20' }}
          >
            <div className="h-2 w-2 rounded-full animate-pulse" style={{ background: '#f59e0b' }} />
            <span className="text-sm font-medium" style={{ color: '#f59e0b' }}>
              {pendingCount} pendente{pendingCount > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="mb-4 flex items-center gap-2">
        {(['pendente', 'all', 'aprovado', 'rejeitado'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            style={{
              background: filter === f ? '#334155' : 'transparent',
              color: filter === f ? '#f1f5f9' : '#64748b',
            }}
          >
            {f === 'all' ? 'Todas' : f === 'pendente' ? 'Pendentes' : f === 'aprovado' ? 'Aprovadas' : 'Rejeitadas'}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="animate-pulse rounded-xl p-8" style={{ background: '#1e293b' }}>
          <div className="h-48" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl" style={{ background: '#1e293b' }}>
          <UserPlus className="h-12 w-12 mb-3" style={{ color: '#475569' }} />
          <p className="text-base" style={{ color: '#94a3b8' }}>
            {filter === 'pendente' ? 'Nenhuma solicitação pendente' : 'Nenhuma solicitação encontrada'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((req) => {
            const sc = statusConfig[req.status] || statusConfig.pendente!;
            const StatusIcon = sc.icon;

            return (
              <div
                key={req.id}
                className="rounded-xl border p-5"
                style={{ background: '#1e293b', borderColor: '#334155' }}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-base font-semibold" style={{ color: '#f1f5f9' }}>
                        {req.nome_fantasia}
                      </h3>
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                        style={{ background: sc.bg, color: sc.color }}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {sc.label}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span style={{ color: '#64748b' }}>Telefone: </span>
                        <a
                          href={`https://wa.me/55${req.telefone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                          style={{ color: '#10b981' }}
                        >
                          {req.telefone}
                        </a>
                      </div>
                      <div>
                        <span style={{ color: '#64748b' }}>Email: </span>
                        <span style={{ color: '#94a3b8' }}>{req.email}</span>
                      </div>
                      <div>
                        <span style={{ color: '#64748b' }}>Plano: </span>
                        <span style={{ color: '#94a3b8' }}>{req.plano}</span>
                      </div>
                    </div>

                    {(req as any).responsavel && (
                      <p className="text-sm mt-1">
                        <span style={{ color: '#64748b' }}>Responsável: </span>
                        <span style={{ color: '#94a3b8' }}>{(req as any).responsavel}</span>
                      </p>
                    )}

                    {(req as any).cidade && (
                      <p className="text-sm mt-1">
                        <span style={{ color: '#64748b' }}>Cidade: </span>
                        <span style={{ color: '#94a3b8' }}>{(req as any).cidade}</span>
                      </p>
                    )}

                    {(req as any).endereco && (
                      <p className="text-sm mt-1">
                        <span style={{ color: '#64748b' }}>Endereço: </span>
                        <span style={{ color: '#94a3b8' }}>{(req as any).endereco}</span>
                      </p>
                    )}

                    {req.cnpj && (
                      <p className="text-sm mt-1">
                        <span style={{ color: '#64748b' }}>CNPJ: </span>
                        <span className="font-mono" style={{ color: '#94a3b8' }}>{req.cnpj}</span>
                      </p>
                    )}

                    <p className="text-xs mt-2" style={{ color: '#475569' }}>
                      Solicitado em {formatDate(req.created_at)}
                    </p>
                  </div>

                  {/* Actions */}
                  {req.status === 'pendente' && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleAction(req.id, 'aprovado')}
                        disabled={processing === req.id}
                        className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
                        style={{ background: '#10b981' }}
                      >
                        <Check className="h-4 w-4" />
                        Aprovar
                      </button>
                      <button
                        onClick={() => handleAction(req.id, 'rejeitado')}
                        disabled={processing === req.id}
                        className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                        style={{ background: '#334155', color: '#ef4444' }}
                      >
                        <X className="h-4 w-4" />
                        Rejeitar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
